import { alpha } from '@mui/material/styles';

// ----------------------------------------------------------------------
// pwm (GLP-1 Wellness) Palette
//
// 디자인 시스템 컬러 토큰 기반 (Figma "Colors" 시안).
// 컨셉: 웰니스/식물 톤. Sage green(메인) + Coral(서브) + warm gray.
// - 의료기기 X (DTx 아님) → 강한 의료/제약 톤 배제
// - 익명성 컨셉 → 과한 강조색 자제
// ----------------------------------------------------------------------

export type ColorSchema = 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error';

declare module '@mui/material/styles/createPalette' {
  interface TypeBackground {
    neutral: string;
  }
  interface SimplePaletteColorOptions {
    lighter: string;
    darker: string;
  }
  interface PaletteColor {
    lighter: string;
    darker: string;
  }

  interface Palette {
    // 브랜드 컬러 풀 스케일 (raw 토큰 접근용)
    sage: typeof SAGE;
    coral: typeof CORAL;
    // Cream & Warm (배경/카드 톤)
    cream: typeof CREAM;
    // Info / Data 시각화용
    slate: typeof SLATE;
    // System 시맨틱 컬러
    system: typeof SYSTEM;
  }
  interface PaletteOptions {
    sage?: typeof SAGE;
    coral?: typeof CORAL;
    cream?: typeof CREAM;
    slate?: typeof SLATE;
    system?: typeof SYSTEM;
  }
}

// ----------------------------------------------------------------------
// Sage green (Main Color · brand)
// ----------------------------------------------------------------------
export const SAGE = {
  50: '#F4F7F3',
  100: '#E5EDE2',
  200: '#C8D9C2',
  300: '#A5BF9C',
  400: '#7DA471',
  500: '#4F7847', // Main / Sage
  600: '#466A3D',
  700: '#364F30',
};

// ----------------------------------------------------------------------
// Coral (Sub Color)
// ----------------------------------------------------------------------
export const CORAL = {
  100: '#FEEDE7',
  200: '#FCE1D6',
  300: '#F5B5A2',
  400: '#ED8870', // Sub / Coral
  500: '#D16554',
  600: '#B5482F',
  700: '#8E3520',
};

// ----------------------------------------------------------------------
// Gray (warm neutral)
// ----------------------------------------------------------------------
export const GREY = {
  0: '#FFFFFF',
  50: '#F7F7F6',
  100: '#EEEEED',
  200: '#DCDCD9',
  300: '#C8C8C5',
  400: '#ABABA8',
  500: '#888885',
  600: '#6F6F6C',
  700: '#555553',
  800: '#3A3A38',
  900: '#2A2A28',
  1000: '#1F1F1D',
};

// ----------------------------------------------------------------------
// Cream & Warm (배경/카드 톤)
// ----------------------------------------------------------------------
export const CREAM = {
  warm200: '#E7DCCA',
  cream100: '#F4EFE5',
  cream50: '#FBF8F4',
};

// 카드·표면 화이트: 순백(#FFF) 대신 눈부심 적은 따뜻한 오프화이트. 페이지 배경(Cream)은 그대로 유지.
export const SURFACE_WHITE = '#FAF9F6';

// ----------------------------------------------------------------------
// Info / Data (데이터 시각화 · 슬레이트 블루)
// ----------------------------------------------------------------------
export const SLATE = {
  blue: '#445F8C',
  light: '#E9EEF7',
};

// ----------------------------------------------------------------------
// System (시맨틱 상태 컬러)
// ----------------------------------------------------------------------
export const SYSTEM = {
  danger: '#B91C1C',
  warning: '#B45309',
  attention: '#ED8870',
  info: '#445F8C',
  success: '#466A3D',
  dangerBg: '#FEE2E2',
  warningBg: '#FEF3C7',
  attentionBg: '#FCE1D6',
  infoBg: '#E9EEF7',
  successBg: '#E5EDE2',
};

// ----------------------------------------------------------------------
// MUI 시맨틱 매핑 (lighter / light / main / dark / darker)
// ----------------------------------------------------------------------
const PRIMARY = {
  lighter: SAGE[100],
  light: SAGE[300],
  main: SAGE[500],
  dark: SAGE[600],
  darker: SAGE[700],
  contrastText: '#fff',
};

const SECONDARY = {
  lighter: CORAL[100],
  light: CORAL[300],
  main: CORAL[400],
  dark: CORAL[500],
  darker: CORAL[700],
  contrastText: '#fff',
};

const INFO = {
  lighter: SLATE.light,
  light: '#A9B7D2',
  main: SLATE.blue,
  dark: '#324569',
  darker: '#1B2638',
  contrastText: '#fff',
};

const SUCCESS = {
  lighter: SYSTEM.successBg,
  light: SAGE[300],
  main: SYSTEM.success,
  dark: SAGE[700],
  darker: '#243A1F',
  contrastText: '#fff',
};

const WARNING = {
  lighter: SYSTEM.warningBg,
  light: '#E9A857',
  main: SYSTEM.warning,
  dark: '#823B06',
  darker: '#4A2103',
  contrastText: '#fff',
};

const ERROR = {
  lighter: SYSTEM.dangerBg,
  light: '#E59A9A',
  main: SYSTEM.danger,
  dark: '#8A1515',
  darker: '#4D0B0B',
  contrastText: '#fff',
};

const COMMON = {
  common: { black: '#000', white: '#fff' },
  primary: PRIMARY,
  secondary: SECONDARY,
  info: INFO,
  success: SUCCESS,
  warning: WARNING,
  error: ERROR,
  grey: GREY,
  sage: SAGE,
  coral: CORAL,
  cream: CREAM,
  slate: SLATE,
  system: SYSTEM,
  divider: alpha(GREY[500], 0.24),
  action: {
    hover: alpha(GREY[500], 0.08),
    selected: alpha(GREY[500], 0.16),
    disabled: alpha(GREY[500], 0.8),
    disabledBackground: alpha(GREY[500], 0.24),
    focus: alpha(GREY[500], 0.24),
    hoverOpacity: 0.08,
    disabledOpacity: 0.48,
  },
};

export default function palette(themeMode: 'light' | 'dark') {
  const light = {
    ...COMMON,
    mode: 'light',
    text: {
      primary: GREY[1000],
      secondary: GREY[600],
      disabled: GREY[400],
    },
    background: {
      paper: SURFACE_WHITE,
      default: CREAM.cream50,
      neutral: CREAM.cream100,
    },
    action: {
      ...COMMON.action,
      active: GREY[600],
    },
  } as const;

  const dark = {
    ...COMMON,
    mode: 'dark',
    text: {
      primary: '#fff',
      secondary: GREY[400],
      disabled: GREY[600],
    },
    background: {
      paper: GREY[900],
      default: GREY[1000],
      neutral: alpha(GREY[500], 0.16),
    },
    action: {
      ...COMMON.action,
      active: GREY[500],
    },
  } as const;

  return themeMode === 'light' ? light : dark;
}
